import {Fragment, useContext, useEffect, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {useLocation} from 'react-router-dom';

import {PageHeader, Tab, Tabs, notifier} from 'components';
import {UserContext} from 'context';
import {usePrevious} from 'hooks';
import {TRANSLATION_KEYS} from 'locale';
import {userAuthUtils} from 'utils';

import {ClientSessionCustomerLogo} from './ClientSessionCustomerLogo';
import {InteractiveChat} from './InteractiveChat';
import {LiveHelp} from './LiveHelp';
import {PreSessionFlow} from './PreSessionFlow';
import {WhiteLabel as WhiteLabelComponent} from './WhiteLabel';

const T_KEYS = TRANSLATION_KEYS.USER.CONTENT.CLIENT_SESSION;

export const ClientSession: React.FC = () => {
    const {t} = useTranslation();
    const location = useLocation<{chatGptAdded: boolean}>();
    // Redirect from ChatGPT page
    // @see https://eazyshow.atlassian.net/browse/LVC-2317
    const chatGptAdded = useMemo(() => location.state?.chatGptAdded, [location]);
    const prevChatGptAdded = usePrevious(chatGptAdded);
    const defaultTab = useMemo(() => chatGptAdded ? t(T_KEYS.TABS.LIVE_HELP) : null, [chatGptAdded, t]);

    const {customerLogo, whiteLabel, liveHelp, interactiveChat, preSessionFlow} = useMemo(() =>
        userAuthUtils.getUserPermissions().content.clientSession, []);

    const {settings: {featuresAvailability}} = useContext(UserContext);
    const preSessionFlowTabVisible = !!preSessionFlow?.enabled && !!featuresAvailability?.preSessionFlow;

    useEffect(() => {
        if (chatGptAdded && !prevChatGptAdded) {
            notifier.success({title: t(T_KEYS.CHAT_GPT_ADDED)});
        }
    }, [chatGptAdded, t, prevChatGptAdded]);

    return (
        <Fragment>
            <PageHeader title={t(T_KEYS.TITLE)} description={t(T_KEYS.DESCRIPTION)} />
            <Tabs defaultTabName={defaultTab}>
                <Tab name={t(T_KEYS.TABS.CUSTOMER_LOGO)} hidden={!customerLogo?.enabled}>
                    <ClientSessionCustomerLogo />
                </Tab>
                <Tab name={t(T_KEYS.TABS.WHITE_LABEL)} hidden={!whiteLabel?.enabled}>
                    <WhiteLabelComponent />
                </Tab>
                <Tab name={t(T_KEYS.TABS.LIVE_HELP)} hidden={!liveHelp?.enabled}>
                    <LiveHelp />
                </Tab>
                <Tab name={t(T_KEYS.TABS.INTERACTIVE_CHAT)} hidden={!interactiveChat?.enabled}>
                    <InteractiveChat />
                </Tab>
                <Tab name={t(T_KEYS.TABS.PRE_SESSION_FLOW)} hidden={!preSessionFlowTabVisible}>
                    <PreSessionFlow />
                </Tab>
            </Tabs>
        </Fragment>
    );
};